import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { finalize } from 'rxjs';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import { AppContextService } from '@olv/ui-shared/services/app-context.service';
import { AuthService } from '@olv/ui-shared/services/auth.service';
import { DatePickerService, RangeOfDate } from '@olv/ui-shared/services/date-picker.service';
import { ReportsService } from '@olv/ui-shared/services/reports.service';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';
import { DatePickerComponent } from '@olv/ui-shared/ui-shared/date-picker/date-picker.component';
import { DropdownComponent } from '@olv/ui-shared/ui-shared/dropdown/dropdown.component';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-reports-ledger',
  imports: [HasPermissionDirective, DropdownComponent, FormsModule, DatePickerComponent, ButtonComponent],
  templateUrl: './reports-ledger.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './reports-ledger.component.scss',
})
export class ReportsLedgerComponent implements OnInit {
  private reportsService = inject(ReportsService);

  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;
  private auth = inject(AuthService);
  private readonly appCtx = inject(AppContextService);
  private readonly datePickerService = inject(DatePickerService);
  private readonly appName = this.appCtx.getAppName();
  isLoading = false;
  isSettlementApp = this.appName === 'settlement';

  rangeStart: Date | null = null;
  rangeEnd: Date | null = null;
  dateFilter = {
    key: 'dateLedger',
    label: 'Date',
    start: null as Date | null,
    end: null as Date | null,
  };

  selectedCurrencies: string[] = [];
  currencyOptions: { value: any; label: string }[] = [];

  merchantOptions: { value: string; label: string }[] = [];
  selectedMerchant: string | undefined = undefined;

  ngOnInit(): void {
    if (this.auth.hasPermission(Resources.Reports, Scopes.MerchantsFilter) && this.isSettlementApp) {
      this.loadMerchants();
    }
    this.loadLedgerCurrencies();
  }

  loadMerchants(): void {
    this.isLoading = true;

    this.reportsService
      .getMerchantFilter()
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (merchants) => {
          this.merchantOptions = merchants.map((m) => ({
            value: m.id,
            label: m.name,
          }));
        },
        error: (err) => {
          console.error('Error loading merchants:', err);
        },
      });
  }

  loadLedgerCurrencies(): void {
    this.reportsService.getLedgerCurrencies().subscribe({
      next: (items) => {
        this.currencyOptions = (items || []).map((c) => ({
          value: c.id,
          label: c.name,
        }));
        this.selectedCurrencies = [];
      },
      error: (err) => {
        console.error('Error loading ledger currencies:', err);
      },
    });
  }

  onDateRangeSelected(range: RangeOfDate): void {
    const selectedRange = this.datePickerService.dateRangeSelected(range);

    this.rangeStart = selectedRange.start;
    this.rangeEnd = selectedRange.end;
    this.dateFilter = { ...this.dateFilter, start: selectedRange.start, end: selectedRange.end };
  }

  onGenerate(event?: Event) {
    event?.preventDefault();

    if (this.isGenerateDisabled || this.isLoading) {
      return;
    }

    const startDate = this.rangeStart ? this.toYmd(this.rangeStart) : '';
    const endDate = this.rangeEnd ? this.toYmd(this.rangeEnd) : '';
    const merchantId = this.selectedMerchant as string; // guid
    const currencies = (this.selectedCurrencies || []).map((c) => parseInt(c as string, 10));

    const payload = {
      merchantId,
      currencies,
      startDate,
      endDate,
    };

    this.isLoading = true;
    this.reportsService
      .exportLedger(payload)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (result) => {
          if (!result) {
            console.error('Export failed.');
            return;
          }
          const { blob, filename } = result;
          this.downloadBlob(blob, filename);
        },
        error: (err) => {
          console.error('Error exporting ledger:', err);
        },
      });
  }

  private toYmd(date: Date): string {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    return d.toISOString().slice(0, 10);
  }

  private downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  get isGenerateDisabled(): boolean {
    const isMerchantRequired = this.isSettlementApp === true;

    return (
      !this.rangeStart ||
      !this.rangeEnd ||
      (isMerchantRequired && !this.selectedMerchant) ||
      !Array.isArray(this.selectedCurrencies) ||
      this.selectedCurrencies.length === 0
    );
  }

  get generateButtonState(): 'disabled' | 'default' {
    return this.isGenerateDisabled || this.isLoading ? 'disabled' : 'default';
  }
}
